import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, UploadCloud, FileText, Image as ImageIcon, Replace } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useInsuredPolicies, useUploadInsuredDocument } from "@/lib/api";
import { apiFetch } from "@/lib/queryClient";
import {
  formatEffectiveDate,
  formatHumanFileSize,
  isImageMime,
  isPdfMime,
} from "./document-helpers";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insuredId: string;
  prefillFile?: File | null;
}

const MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB inclusive
const NAME_MAX = 200;
// CSR-1257: Radix <SelectItem> can't use an empty-string value, so the
// "no policy" choice uses this sentinel and maps back to null on submit.
const NO_POLICY = "__none__";

export function UploadDocumentDialog({ open, onOpenChange, insuredId, prefillFile }: Props) {
  const { toast } = useToast();
  const uploadMutation = useUploadInsuredDocument(insuredId);
  // CSR-1257: optional policy link. Lists all of the insured's policies (any
  // status); blank by default so behavior is unchanged when left untouched.
  const { data: policiesResp } = useInsuredPolicies(insuredId);
  const policies = policiesResp?.data ?? [];
  const [name, setName] = useState("");
  const [policyId, setPolicyId] = useState<string>(NO_POLICY);
  const [file, setFile] = useState<File | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Adopt the prefill file when the dialog opens via drag-drop.
  useEffect(() => {
    if (!open) return;
    if (prefillFile) {
      handleFileSelected(prefillFile);
    }
    // Intentionally only react to `open`/`prefillFile` — `handleFileSelected`
    // is a stable closure over local setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillFile]);

  // Reset state when dialog closes.
  useEffect(() => {
    if (open) return;
    setName("");
    setPolicyId(NO_POLICY);
    setFile(null);
    setNameError(null);
    setFileError(null);
    setIsUploading(false);
  }, [open]);

  // Build / revoke a blob URL for the preview pane.
  const previewUrl = useMemo(() => {
    if (!file) return null;
    return URL.createObjectURL(file);
  }, [file]);
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFileSelected = (chosen: File) => {
    if (chosen.size > MAX_FILE_BYTES) {
      // Layer 1 of the dual-guard. Layer 2 lives on the server.
      setFile(null);
      setFileError("File is too large. Maximum size is 50 MB.");
      return;
    }
    // CSR-1300: pre-fill the name with the filename, but never clobber a
    // custom name the agent already typed (a previous auto-fill counts as
    // untouched, so changing files updates it).
    if (!name.trim() || name === file?.name) {
      setName(chosen.name);
    }
    setFile(chosen);
    setFileError(null);
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (value.length <= NAME_MAX) {
      setNameError(null);
    }
  };

  const handleSave = async () => {
    let blocked = false;
    if (name.length > NAME_MAX) {
      setNameError(`Document name must be ${NAME_MAX} characters or fewer`);
      blocked = true;
    } else {
      setNameError(null);
    }
    if (!file) {
      setFileError("Select a file to upload.");
      blocked = true;
    }
    if (blocked || !file) return;

    // CSR-1300: the name is optional in the UI; a blank name falls back to
    // the filename so the server contract (non-empty name) is unchanged.
    const label = name.trim() || file.name;

    setIsUploading(true);
    try {
      // 1. Request a presigned upload URL. CSR-1807 — surface the server's
      // error (e.g. the 503 DC-unavailable message or a 404) in the toast.
      const urlRes = await apiFetch(`/api/insureds/${insuredId}/documents/upload-url`, {
        method: "POST",
        credentials: "include",
      });
      if (!urlRes.ok) {
        const body = await urlRes.json().catch(() => null);
        throw new Error(
          typeof body?.error === "string" && body.error
            ? body.error
            : "Failed to obtain upload URL",
        );
      }
      const { uploadURL, objectPath } = (await urlRes.json()) as {
        uploadURL: string;
        objectPath: string;
      };

      // 2. PUT the file body directly to GCS / local-FS.
      const storageFailure =
        "Could not upload the file to storage. Check your connection and try again.";
      // CSR-2309 — native fetch, NOT apiFetch: this URL is the storage provider's
      // own signed URL, and GCS's XML-API CORS rejects a credentialed request, so
      // forcing `credentials: "include"` here fails every upload. apiFetch is for
      // same-origin AMS endpoints only.
      const putRes = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
      }).catch(() => {
        throw new Error(storageFailure);
      });
      if (!putRes.ok) throw new Error(storageFailure);

      // 3. Persist the row.
      await uploadMutation.mutateAsync({
        name: label,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || "application/octet-stream",
        objectPath,
        policyId: policyId === NO_POLICY ? null : policyId,
      });

      toast({
        title: "Document Uploaded",
        description: `${label} has been saved.`,
      });
      onOpenChange(false);
    } catch (err) {
      console.error("Document upload failed:", err);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description:
          err instanceof Error && err.message
            ? err.message
            : "Failed to upload document. Please try again.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const canSave = !!file && !nameError && !fileError && !isUploading;

  return (
    <Dialog open={open} onOpenChange={(next) => !isUploading && onOpenChange(next)}>
      <DialogContent
        className="max-w-3xl"
        data-testid="dialog-upload-document"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload a file and optionally give it a descriptive name.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="upload-doc-name">Document Name</Label>
              <Input
                id="upload-doc-name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                maxLength={NAME_MAX}
                disabled={isUploading}
                placeholder="e.g. Signed E-Sig Form"
                data-testid="input-document-name"
                aria-invalid={!!nameError}
              />
              <p className="text-xs text-muted-foreground">
                A descriptive label users will see in the documents list.
                Defaults to the filename.
              </p>
              {nameError && (
                <p
                  className="text-sm text-destructive"
                  data-testid="error-document-name"
                  role="alert"
                >
                  {nameError}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>
                File <span className="text-destructive">*</span>
              </Label>
              {file ? (
                <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <FileText
                      className="w-4 h-4 text-muted-foreground shrink-0"
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatHumanFileSize(file.size)}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isUploading}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="button-change-file"
                  >
                    <Replace className="w-4 h-4 mr-1" aria-hidden="true" />
                    Change
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  disabled={isUploading}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="button-choose-file"
                >
                  <UploadCloud className="w-4 h-4 mr-2" aria-hidden="true" />
                  Choose File
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="sr-only"
                onChange={(e) => {
                  const chosen = e.target.files?.[0];
                  if (chosen) handleFileSelected(chosen);
                  e.currentTarget.value = "";
                }}
                data-testid="input-upload-file"
              />
              {fileError && (
                <p
                  className="text-sm text-destructive"
                  data-testid="error-document-file"
                  role="alert"
                >
                  {fileError}
                </p>
              )}
            </div>

            {/* CSR-1257: optional policy association */}
            <div className="space-y-2">
              <Label htmlFor="upload-doc-policy">Policy</Label>
              <Select value={policyId} onValueChange={setPolicyId} disabled={isUploading}>
                <SelectTrigger id="upload-doc-policy" data-testid="select-document-policy">
                  <SelectValue placeholder="No policy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_POLICY}>No policy</SelectItem>
                  {policies.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {`${p.policyNumber} — ${p.carrier} — ${formatEffectiveDate(p.effectiveDate)}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Optionally associate this document with one of the insured's policies.
              </p>
            </div>
          </div>

          {/* Preview */}
          <div className="border rounded-md bg-muted/20 min-h-[280px] flex items-center justify-center overflow-hidden">
            {!file || !previewUrl ? (
              <p className="text-sm text-muted-foreground p-6 text-center">
                Select a file to see a preview.
              </p>
            ) : isPdfMime(file.type) ? (
              <iframe
                src={previewUrl}
                className="w-full h-[280px] md:h-[420px]"
                title="PDF preview"
                data-testid="upload-preview-pdf"
              />
            ) : isImageMime(file.type) ? (
              <img
                src={previewUrl}
                alt="Preview"
                className="max-h-[420px] max-w-full object-contain"
                data-testid="upload-preview-image"
              />
            ) : (
              <div
                className="flex flex-col items-center gap-2 p-6 text-center text-sm text-muted-foreground"
                data-testid="upload-preview-fallback"
              >
                <ImageIcon className="w-8 h-8 text-muted-foreground/60" aria-hidden="true" />
                <p>Preview not available for this file type</p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isUploading}
            data-testid="button-cancel-upload"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            data-testid="button-save-document"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                Uploading...
              </>
            ) : (
              "Save Document"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
